import qs from 'qs';
import { OffsetPaginationInput } from '@app/data/inputs';
import { CollectionResource } from '@app/data/resources';
import { AxiosInstance } from 'axios';
import {
    CreateIllustrationItemInput,
    CreateIllustrationVariantInput,
    IllustrationFilterInput,
    UpdateIllustrationItemInput,
    UpdateIllustrationVariantInput,
} from './inputs';
import { IllustrationLibCategoryRef, IllustrationLibItemRef, IllustrationLibItemVariantRef } from './refs';
import { CategoryResource, IllustrationItemResource, TagResource } from './resources';

export default class IllustrationLib {
    private basePath = '/api/illustration-lib';
    private http: AxiosInstance;

    constructor(http: AxiosInstance) {
        this.http = http;
    }

    // Get list of all tags
    getTagList = async (): Promise<CollectionResource<TagResource, {}>> => {
        const { data } = await this.http.get(`${this.basePath}/tag`);
        return data;
    };

    // Get list of all categories
    getCategoryList = async (): Promise<CollectionResource<CategoryResource, {}>> => {
        const { data } = await this.http.get(`${this.basePath}/category`);
        return data;
    };

    // Create new library category
    createCategory = async (): Promise<IllustrationLibCategoryRef> => {
        const { data } = await this.http.post(`${this.basePath}/category`);
        return data;
    };

    // Update category
    updateCategory = async (id: ID): Promise<IllustrationLibCategoryRef> => {
        const { data } = await this.http.put(`${this.basePath}/category/${id}`);
        return data;
    };

    // Archive category
    archiveCategory = async (id: ID): Promise<IllustrationLibCategoryRef> => {
        const { data } = await this.http.delete(`${this.basePath}/category/${id}`);
        return data;
    };

    // Restore archived category
    restoreCategory = async (id: ID): Promise<IllustrationLibCategoryRef> => {
        const { data } = await this.http.post(`${this.basePath}/${id}/restore`);
        return data;
    };

    // Get list of teams
    getItemList = async (
        pagination: OffsetPaginationInput,
        filter?: IllustrationFilterInput
    ): Promise<CollectionResource<IllustrationItemResource, {}>> => {
        const query = qs.stringify({ ...pagination, ...(filter || {}) });

        const { data } = await this.http.get(`${this.basePath}/item?${query}`);
        return data;
    };

    // Create new item
    createItem = async (input: CreateIllustrationItemInput): Promise<IllustrationLibItemRef> => {
        const { data } = await this.http.post(`${this.basePath}/item`, input);
        return data;
    };

    // Get single item
    getItem = async (id: ID): Promise<IllustrationItemResource> => {
        const { data } = await this.http.get(`${this.basePath}/item/${id}`);
        return data;
    };

    // Update item
    updateItem = async (id: ID, input: UpdateIllustrationItemInput): Promise<IllustrationLibItemRef> => {
        const { data } = await this.http.put(`${this.basePath}/item/${id}`, input);
        return data;
    };

    // Archive item
    archiveItem = async (id: ID): Promise<IllustrationLibItemRef> => {
        const { data } = await this.http.delete(`${this.basePath}/item/${id}`);
        return data;
    };

    // Restore item
    restoreItem = async (id: ID): Promise<IllustrationLibItemRef> => {
        const { data } = await this.http.delete(`${this.basePath}/item/${id}/restore`);
        return data;
    };

    // Create new variant
    createVariant = async (input: CreateIllustrationVariantInput): Promise<IllustrationLibItemVariantRef> => {
        const { data } = await this.http.post(`${this.basePath}/variant`, input);
        return data;
    };

    // Update variant
    updateVariant = async (id: ID, input: UpdateIllustrationVariantInput): Promise<IllustrationLibItemVariantRef> => {
        const { data } = await this.http.put(`${this.basePath}/variant/${id}`, input);
        return data;
    };

    // Archive variant
    archiveVariant = async (id: ID): Promise<IllustrationLibItemVariantRef> => {
        const { data } = await this.http.delete(`${this.basePath}/variant/${id}`);
        return data;
    };

    // Restore variant
    restoreVariant = async (id: ID): Promise<IllustrationLibItemVariantRef> => {
        const { data } = await this.http.post(`${this.basePath}/variant/${id}/restore`);
        return data;
    };
}
